import React, { createContext, useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import CenteredSpinner from '../components/ui/loadingspinner';

const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:3004' : process.env.REACT_APP_BACKEND_URL;
const JWT_REFRESH_INTERVAL = 14 * 60 * 1000;

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  googleId?: string;
  subscriptionTier?: string;
  credits?: number;
  storageUsed?: number;
  processingMinutes?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loginWithGoogle: () => void;
  logout: () => void;
  refreshJWT: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
let refreshTokenQueue: Promise<string> | null = null;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const handleError = (error: any, defaultMessage: string) => {
    console.error('Error details:', error);
    const errorMessage = error?.response?.data?.error || error?.message || defaultMessage;
    toast.error(errorMessage);
    if (error?.response?.status === 401) handleAuthError();
  };

  const handleAuthError = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const refreshJWT = async (): Promise<string> => {
    if (refreshTokenQueue) return refreshTokenQueue;

    refreshTokenQueue = new Promise(async (resolve, reject) => {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token available');

        const { data } = await axios.post(`${BACKEND_URL}/refresh-token`, { refreshToken });
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);

        resolve(data.accessToken);
      } catch (error) {
        handleError(error, 'Failed to refresh token');
        handleAuthError();
        reject(error);
      } finally {
        refreshTokenQueue = null;
      }
    });

    return refreshTokenQueue;
  };

  const syncUserWithBackend = async (userData: Partial<User>) => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/syncuser`, userData);
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      return data.user;
    } catch (error) {
      handleError(error, 'Failed to sync user data');
      throw error;
    }
  };

  const getUserInfoFromGoogle = async (accessToken: string) => {
    try {
      const { data } = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return data;
    } catch (error) {
      handleError(error, 'Failed to get user info from Google');
      throw error;
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        const googleUser = await getUserInfoFromGoogle(tokenResponse.access_token);
        const syncedUser = await syncUserWithBackend({
          email: googleUser.email,
          name: googleUser.name,
          googleId: googleUser.sub,
          picture: googleUser.picture
        });
        setUser(syncedUser);
        setIsAuthenticated(true);
        navigate('/dashboard');
        toast.success(`Welcome${syncedUser.name ? `, ${syncedUser.name}` : ''}!`);
      } catch (error) {
        handleError(error, 'Google login failed');
      } finally {
        setIsLoading(false);
      }
    },
    onError: (error) => {
      handleError(error, 'Google login failed');
      setIsLoading(false);
    }
  });

  useEffect(() => {
    const checkSessionAndSetupRefresh = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return setIsLoading(false);

      try {
        const { data } = await axios.get(`${BACKEND_URL}/me`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        setUser(data.user);
        setIsAuthenticated(true);
        const refreshInterval = setInterval(refreshJWT, JWT_REFRESH_INTERVAL);
        return () => clearInterval(refreshInterval);
      } catch {
        try {
          await refreshJWT();
          const { data } = await axios.get(`${BACKEND_URL}/me`);
          setUser(data.user);
          setIsAuthenticated(true);
        } catch {
          handleAuthError();
        }
      } finally {
        setIsLoading(false);
      }
    };
    checkSessionAndSetupRefresh();
  }, []);

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loginWithGoogle: handleGoogleLogin, logout, refreshJWT }}>
      {isLoading ? <CenteredSpinner /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
